import { useEffect, useRef } from 'react';

/**
 * Mirrors the getTenantSlug() logic from src/utils/axios/private.js.
 * Extracts the subdomain-based tenant slug from the current hostname.
 * Returns null on localhost / non-subdomain hosts.
 */
const getTenantSlug = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const ignore = ['www', 'dev', 'test', 'staging', 'prod'];
    if (parts.length <= 2) return null;
    if (ignore.includes(parts[0])) return null;
    return parts[0];
};

/**
 * useTerminateOnExit
 *
 * Sends a fire-and-forget termination request to the backend when the candidate
 * closes the tab, closes the browser, or refreshes the page mid-interview.
 *
 * Uses fetch() with { keepalive: true, credentials: 'include' } instead of
 * navigator.sendBeacon() because:
 *   1. sendBeacon does NOT send cookies on cross-origin requests (dev: port 3000 → 8000)
 *   2. The interview_id lives in an HttpOnly cookie that JS cannot read
 *   3. fetch + keepalive + credentials:'include' correctly sends HttpOnly cookies
 *      cross-origin when the server has CORS_ALLOW_CREDENTIALS = True (which it does)
 *   4. keepalive:true ensures the request is completed even after the page unloads
 *
 * @param {Object}  options
 * @param {boolean} options.isActive   - Only fire when true. Set to false after the
 *                                       interview completes normally to prevent a
 *                                       false termination signal on the success redirect.
 * @param {string}  options.endpoint   - Full URL of the terminate endpoint.
 *
 * @returns {{ disarm: () => void }}
 *   disarm() — call this SYNCHRONOUSLY before any programmatic navigate() that
 *   should NOT trigger a termination (e.g. tab-switch redirect, inactivity,
 *   TERMINATED-on-load detection). It sets isActiveRef immediately so that the
 *   beforeunload handler (which fires synchronously) sees the updated value.
 *   Unlike setState(), this bypasses React's async render cycle entirely.
 */
const useTerminateOnExit = ({ isActive = true, endpoint }) => {
    // Use a ref so the unload handler always reads the latest isActive value
    // without needing to re-register event listeners on every render.
    const isActiveRef = useRef(isActive);

    // Keep ref in sync with the isActive prop (for normal completion flows
    // where the parent sets isActive=false and a re-render happens naturally).
    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    useEffect(() => {
        if (!endpoint) return;

        // Guard against both beforeunload + pagehide both firing for the same exit
        let hasFired = false;

        const sendTerminationRequest = () => {
            if (!isActiveRef.current || hasFired) return;
            hasFired = true;

            try {
                // Build headers — include X-Tenant-Slug so TenantMiddleware can resolve
                // tenant_id correctly (same logic as the private axios instance).
                const headers = { 'Content-Type': 'application/json' };
                const tenantSlug = getTenantSlug();
                if (tenantSlug) {
                    headers['X-Tenant-Slug'] = tenantSlug;
                }

                // fetch with keepalive:true is guaranteed to complete even after page close.
                // credentials:'include' sends HttpOnly cookies cross-origin (requires
                // CORS_ALLOW_CREDENTIALS=True on the server, which is already set).
                fetch(endpoint, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ "reason": 'tab_close' }),
                    keepalive: true,
                    credentials: 'include',
                }).catch((err) => {
                    // Errors here are expected during page teardown — safe to swallow
                    console.warn('[useTerminateOnExit] fetch keepalive error (expected on unload):', err);
                });
            } catch (err) {
                console.error('[useTerminateOnExit] Failed to send termination request:', err);
            }
        };

        window.addEventListener('beforeunload', sendTerminationRequest);
        window.addEventListener('pagehide', sendTerminationRequest);

        return () => {
            window.removeEventListener('beforeunload', sendTerminationRequest);
            window.removeEventListener('pagehide', sendTerminationRequest);
        };
        // Only re-register if endpoint changes; isActive is tracked via ref.
    }, [endpoint]);

    /**
     * disarm() — synchronously disables the beacon.
     *
     * MUST be called before any programmatic navigate("/restriction") that is
     * NOT a genuine manual exit (tab close / browser close). Calling setState
     * is NOT sufficient because React state updates are async and the effect
     * that syncs the ref runs after paint — after beforeunload has already fired.
     *
     * Usage:
     *   const { disarm } = useTerminateOnExit({ isActive: !completed, endpoint });
     *   ...
     *   disarm();               // ← synchronous, immediate
     *   navigate("/restriction", ...);
     */
    const disarm = () => {
        isActiveRef.current = false;
    };

    return { disarm };
};

export default useTerminateOnExit;